import * as Alert from '@/components/ui/alert'
import * as Field from '@/components/ui/field'
import { Heading } from '@/components/ui/heading'
import { Text } from '@/components/ui/text'
import type { CreateSetup, HVFExtrema } from '@hvf/contracts'
import { createForm, getValues, valiForm } from '@modular-forms/solid'
import 'cropperjs/dist/cropper.min.css'
import { Input } from '@/components/ui/input'
import { FieldInformation } from '@components'
import { Info } from 'lucide-solid'
import { For, Show, createEffect, createSignal } from 'solid-js'
import { css } from 'styled-system/css'
import { Divider, Grid } from 'styled-system/jsx'
import * as v from 'valibot'
import type { ScanedDetails } from './scannedDetails'

export type GeneralInformation = Pick<CreateSetup, 'symbol' | 'exchange'>

export type HVFExtremaNumberOnly = {
  [K in keyof HVFExtrema]: number | null
}

// 1. EXTENDED SCHEMA: Added maxLength for safety
const validString = v.pipe(
  v.string(),
  v.minLength(1, 'Must be at least 1 character long'),
  v.maxLength(20, 'Must be 20 characters or less')
);

// 2. EXTENDED SCHEMA: standard positive numbers for prices (allows decimals)
const positivePrice = v.pipe(
  v.number('Must be a number'),
  v.custom<number>((val) => typeof val === 'number' && val > 0, 'Must be greater than 0')
);

// 3. EXTENDED SCHEMA: integers only for timestamps
const validTimestamp = v.pipe(
  v.number('Must be a number'),
  v.integer('Timestamps must be whole numbers'),
  v.custom<number>((val) => typeof val === 'number' && val > 0, 'Must be greater than 0')
);

const HVFExtremaSchema: v.GenericSchema<ScanedDetails> = v.pipe(
  v.object({
    symbol: validString,
    exchange: validString,

    high_1_price: positivePrice,
    high_1_time: validTimestamp,
    high_2_price: positivePrice,
    high_2_time: validTimestamp,
    high_3_price: positivePrice,
    high_3_time: validTimestamp,

    low_1_price: positivePrice,
    low_1_time: validTimestamp,
    low_2_price: positivePrice,
    low_2_time: validTimestamp,
    low_3_price: positivePrice,
    low_3_time: validTimestamp,
  }),

  // --- PRICE VALIDATION (The Y-Axis Pennant Squeeze) ---
  v.partialCheck(
    [['high_1_price'], ['high_2_price']],
    (input) => input.high_1_price > input.high_2_price,
    'High 1 price must be greater than High 2 price'
  ),
  v.partialCheck(
    [['high_2_price'], ['high_3_price']],
    (input) => input.high_2_price > input.high_3_price,
    'High 2 price must be greater than High 3 price'
  ),
  v.partialCheck(
    [['low_1_price'], ['low_2_price']],
    (input) => input.low_1_price < input.low_2_price,
    'Low 1 price must be less than Low 2 price'
  ),
  v.partialCheck(
    [['low_2_price'], ['low_3_price']],
    (input) => input.low_2_price < input.low_3_price,
    'Low 2 price must be less than Low 3 price'
  ),
  v.partialCheck(
    [['high_3_price'], ['low_3_price']],
    (input) => input.high_3_price > input.low_3_price,
    'High 3 price must remain greater than Low 3 price (no crossover)'
  ),

  // --- TIME VALIDATION (The X-Axis Progression) ---
  v.partialCheck(
    [['high_1_time'], ['high_2_time']],
    (input) => input.high_1_time <= input.high_2_time,
    'High 1 time must be before or equal to High 2 time'
  ),
  v.partialCheck(
    [['high_2_time'], ['high_3_time']],
    (input) => input.high_2_time <= input.high_3_time,
    'High 2 time must be before or equal to High 3 time'
  ),
  v.partialCheck(
    [['low_1_time'], ['low_2_time']],
    (input) => input.low_1_time <= input.low_2_time,
    'Low 1 time must be before or equal to Low 2 time'
  ),
  v.partialCheck(
    [['low_2_time'], ['low_3_time']],
    (input) => input.low_2_time <= input.low_3_time,
    'Low 2 time must be before or equal to Low 3 time'
  ),

  // --- DIRECTIONAL LOGIC (Long vs Short Terminations) ---
  v.partialCheck(
    [['high_1_time'], ['low_1_time'], ['high_3_time'], ['low_3_time']],
    (input) => {
      // Determine direction based on which point occurs first
      const isLong = input.high_1_time < input.low_1_time;
      const isShort = input.low_1_time < input.high_1_time;

      if (isLong) {
        // If Long: low_3 must be the final time point on the X-axis
        return input.low_3_time > input.high_3_time;
      }
      if (isShort) {
        // If Short: high_3 must be the final time point on the X-axis
        return input.high_3_time > input.low_3_time;
      }

      // Fallback if H1 and L1 happen at the exact same timestamp
      return true;
    },
    'Terminal Point Error: Long patterns must end on Low 3. Short patterns must end on High 3.'
  )
)

export const Confirm = (props: {
  preview?: string
  onChange: (values: ScanedDetails, isValid: boolean) => void
  isDisabled: boolean
  details: ScanedDetails
}) => {
  const [form, { Form, Field: FormField }] = createForm<ScanedDetails>({
    validate: valiForm(HVFExtremaSchema),
    initialValues: props.details
  })

  // NEW: State to hold our nicely formatted error messages
  const [formErrors, setFormErrors] = createSignal<string[]>([])

  const fields: Array<keyof HVFExtrema> = [
    'high_1_price',
    'high_1_time',
    'low_1_price',
    'low_1_time',
    'high_2_price',
    'high_2_time',
    'low_2_price',
    'low_2_time',
    'high_3_price',
    'high_3_time',
    'low_3_price',
    'low_3_time',
  ]

  createEffect(() => {
    const currentValues = getValues(form) as ScanedDetails
    const result = v.safeParse(HVFExtremaSchema, currentValues)

    // Extract errors if the schema fails
    if (!result.success) {
      const errors = result.issues.map((issue) => {
        const key = issue.path?.[0]?.key as string || 'Field'
        // Format the key nicely (e.g. "high_1_price" -> "High 1 Price")
        const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        return `${formattedKey}: ${issue.message}`
      })

      // Deduplicate in case multiple rules fail on the same field
      setFormErrors(Array.from(new Set(errors)))
    } else {
      // Clear errors when valid!
      setFormErrors([])
    }

    props.onChange(currentValues, result.success)
  })

  return (
    <Grid
      gap={2}
      minHeight='0'
      class={css({ height: '100%', gridTemplateRows: 'auto auto 1fr' })}
    >
      <Divider />
      <FieldInformation information='Match the exchange and Symbol first, e.g. Binance:BTCUSD. Then ensure the price and the time impulses in the image match the text fields, eg PRICE-TIME'>
        <Text children="Ensure the image matches the asscoiated text fields." />
      </FieldInformation>

      <Grid
        class={css({
          gridTemplateColumns: {
            base: '1fr 1fr',
            lgDown: '1fr',
          },
          gridTemplateRows: {
            base: '1fr',
            lgDown: 'auto 1fr',
          },
          justifyItems: 'center',
          alignContent: 'center',
          alignItems: 'center',
          height: '100%',
          overflowY: 'auto',
          minHeight: 0,
          gap: 4
        })}
      >
        <Show when={props.preview}>
          <img
            src={props.preview}
            alt="Cropped preview"
            class={css({
              height: {
                base: '100%',
                lgDown: '250px'
              },
              width: {
                base: 'auto',
                lgDown: '100%'
              },
              maxW: '550px',
              objectFit: 'contain'
            })}
          />
        </Show>

        <Show when={props.isDisabled}>
          <Grid
            class={css({
              height: '100%',
              alignContent: 'center',
              minHeight: 0,
              gap: 4,
            })}
          >
            <Grid gridTemplateColumns="1fr 1fr" gap={4}>
              <FormField name="exchange">
                {(field, formFieldProps) => (
                  <Field.Root invalid={!!field.error}>
                    <Field.Label>Exchange</Field.Label>
                    <Input
                      type="text"
                      {...formFieldProps}
                      value={field.value ?? undefined}
                    />
                  </Field.Root>
                )}
              </FormField>

              <FormField name="symbol">
                {(field, formFieldProps) => (
                  <Field.Root invalid={!!field.error}>
                    <Field.Label>Symbol</Field.Label>
                    <Input
                      type="text"
                      {...formFieldProps}
                      value={field.value ?? undefined}
                    />
                  </Field.Root>
                )}
              </FormField>
            </Grid>

            <Grid gridTemplateColumns="1fr 1fr" columnGap={4} rowGap={2}>
              <Heading textStyle="xs">Price</Heading>
              <Heading textStyle="xs">Time</Heading>

              <For each={fields}>
                {(formField) => (
                  <FormField
                    name={formField}
                    type="number"
                  >
                    {(field, formFieldProps) => (
                      <Field.Root invalid={!!field.error}>
                        <Field.Label>{formField}</Field.Label>
                        <Input
                          {...formFieldProps}
                          value={field.value as any}
                          type="number"
                        />
                      </Field.Root>
                    )}
                  </FormField>
                )}
              </For>
            </Grid>

            {/* NEW: Dynamic Alert Box for Errors */}
            <Show when={formErrors().length > 0}>
              <Alert.Root
                variant='outline'
                status={'error'}
                class={css({ mt: 4 })}
              >
                <Alert.Indicator asChild={(props) => <Info {...props} />} />
                <Alert.Content width="100%">
                  <ul class={css({ paddingLeft: 4, listStyleType: 'disc' })}>
                    <For each={formErrors()}>
                      {(error) => (
                        <li>
                          <Text fontSize="sm">{error}</Text>
                        </li>
                      )}
                    </For>
                  </ul>
                </Alert.Content>
              </Alert.Root>
            </Show>

          </Grid>
        </Show>
      </Grid>
    </Grid>
  )
}
